/** Host OS — Voice Agent. Read-only view of THIS business's Retell AI agent.
 *  Assignment happens admin-side; clients only see their own agent (or a
 *  "being set up" state) — never the list of other agents. */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  PhoneCall, Phone, Loader2, ArrowLeft, Volume2, Globe, RefreshCw,
  CheckCircle2, AlertTriangle, Radio, Clock,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import HostLayout from "@/components/host/HostLayout";
import { HostPageHeader } from "@/components/host/hostUi";
import { API } from "@/App";
import { authHeaders } from "@/lib/firebase";

const StatusBadge = ({ status }) =>
  status === "live" ? (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" data-testid="voice-status-live">
      <Radio className="w-3 h-3" /> Live
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-500/15 text-amber-700 dark:text-amber-300" data-testid="voice-status-draft">
      Not published
    </span>
  );

const Field = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3">
    <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
      <Icon className="w-4 h-4 text-muted-foreground" />
    </div>
    <div className="min-w-0">
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-sm font-medium truncate">{value || "—"}</p>
    </div>
  </div>
);

const HostVoiceAgentPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/host/voice/agent`, { headers });
      setData(await res.json());
    } catch { toast.error("Couldn't load the voice agent"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <HostLayout>
      <div data-testid="host-voice-agent-page">
        <button onClick={() => navigate("/host/ai")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="voice-agent-back">
          <ArrowLeft className="w-4 h-4" /> AI Agents
        </button>

        <HostPageHeader
          title="Voice Agent"
          subtitle="Your AI phone agent, built and managed for you. Here's what's live for your business."
        />

        {loading ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground" data-testid="voice-loading">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : data?.linked && data?.missing ? (
          <Card className="p-6 border-amber-500/40" data-testid="voice-agent-missing">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium">Your voice agent is being updated</h3>
                <p className="text-sm text-muted-foreground mt-1">Hang tight — we're making some changes. It'll be back shortly.</p>
                <Button variant="outline" size="sm" className="mt-3 gap-1.5" onClick={load}><RefreshCw className="w-4 h-4" /> Refresh</Button>
              </div>
            </div>
          </Card>
        ) : data?.linked ? (
          <Card className="p-6" data-testid="voice-agent-overview">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500/20 to-fuchsia-500/10 text-violet-700 dark:text-violet-300 flex items-center justify-center shrink-0">
                  <PhoneCall className="w-7 h-7" />
                </div>
                <div className="min-w-0">
                  <h2 className="font-display text-2xl leading-tight truncate">{data.agent.name}</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">Your AI phone agent</p>
                </div>
              </div>
              <StatusBadge status={data.status} />
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              <Field icon={Phone} label="Phone number" value={(data.phones || []).join(", ") || "No number attached yet"} />
              <Field icon={Volume2} label="Voice" value={data.agent.voice_id} />
              <Field icon={Globe} label="Language" value={data.agent.language} />
              <Field icon={CheckCircle2} label="Status" value={data.status === "live" ? "Live — taking calls" : "Draft — not published"} />
            </div>

            <div className="flex items-center gap-2 mt-6 pt-4 border-t border-border">
              <Button variant="outline" size="sm" onClick={load} className="gap-1.5" data-testid="voice-refresh">
                <RefreshCw className="w-4 h-4" /> Refresh
              </Button>
              <span className="text-xs text-muted-foreground">Need a change to your agent? Just reach out to us.</span>
            </div>
          </Card>
        ) : (
          <Card className="p-10 text-center border-dashed" data-testid="voice-agent-empty">
            <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
              <Clock className="w-7 h-7 text-muted-foreground" />
            </div>
            <h3 className="font-display text-xl">Your voice agent is being set up</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
              We're building your AI phone agent. Once it's ready it'll appear here with its number and details.
            </p>
            <Button variant="outline" size="sm" className="mt-4 gap-1.5" onClick={load}><RefreshCw className="w-4 h-4" /> Refresh</Button>
          </Card>
        )}
      </div>
    </HostLayout>
  );
};

export default HostVoiceAgentPage;
